import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, apiGet } from '../../lib/api';
import { Card } from '../../components/Card';
import { Money } from '../../components/Money';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { PageScaffold } from '../../components/PageScaffold';
import { DataTableCard, STICKY_HEAD } from '../../components/DataTable';

interface VatRegister {
  period: { from: string; to: string; label: string; key: string };
  months: Array<{ key: string; label: string }>;
  lines: Array<{
    id: string;
    orderId: string | null;
    kind: 'invoice' | 'credit_note' | 'adjustment';
    number: string;
    date: string;
    shop: string;
    customer: string;
    subtotal: string;
    vat: string;
    total: string;
  }>;
  totals: { subtotal: string; vat: string; total: string };
}

export function VatPage(): JSX.Element {
  const [month, setMonth] = useState<string>(''); // '' = current month (server default)
  const data = useQuery({
    queryKey: ['vat', month],
    queryFn: () => apiGet<VatRegister>('/finance/vat', month ? { month } : {}),
  });

  const exportExcel = async (): Promise<void> => {
    const params: Record<string, string> = { format: 'excel' };
    if (month) params.month = month;
    const res = await api.get('/finance/vat', { params, responseType: 'blob' });
    const blob = new Blob([res.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vat-register-${data.data?.period.key ?? 'current'}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
  };

  return (
    <PageScaffold
      scroll={false}
      title={<>VAT register · {data.data?.period.label ?? '…'}</>}
      subtitle={
        <>
          Posted straight from the ledger (account 2200) on the invoice tax-point. Credit notes show
          as negative lines, so the period total always ties to the books.
        </>
      }
      actions={
        <Button variant="secondary" onClick={exportExcel}>
          Export Excel
        </Button>
      }
    >
      <Card bodyClassName="p-4" className="shrink-0">
        <div className="flex flex-wrap gap-2 items-center">
          <label htmlFor="vat-month" className="text-xs uppercase tracking-wide text-ink-3 mr-1">
            Month
          </label>
          <select
            id="vat-month"
            value={month || data.data?.period.key || ''}
            onChange={(e) => setMonth(e.target.value)}
            className="px-3 py-1.5 rounded text-sm bg-paper-2 text-ink-2 border border-rule hover:border-accent/40 focus:outline-none focus:border-accent"
          >
            {(data.data?.months ?? []).map((m) => (
              <option key={m.key} value={m.key}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
      </Card>

      {/* ── Headline totals strip — VAT is the hero number ───────────────── */}
      <div className="shrink-0 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="md:col-span-2 rounded-lg bg-ink text-paper-2 p-6">
          <div className="text-[10px] uppercase tracking-wider text-ink-4 font-semibold">
            VAT collected · {data.data?.period.label ?? ''}
          </div>
          <Money
            value={data.data?.totals.vat ?? '0'}
            className="kc-serif text-5xl text-paper-2 mt-2 block"
          />
          <div className="text-xs text-ink-4 mt-3">
            <b className="text-paper-2">{data.data?.lines.length ?? 0}</b> invoice
            {(data.data?.lines.length ?? 0) === 1 ? '' : 's'} recognised
          </div>
        </div>
        <div className="rounded-lg bg-surface border border-rule p-6 flex flex-col justify-between">
          <div className="text-[10px] uppercase tracking-wider text-ink-3 font-semibold">
            Taxable
          </div>
          <Money
            value={data.data?.totals.subtotal ?? '0'}
            className="kc-serif text-3xl text-ink mt-2 block"
          />
          <div className="text-xs text-ink-3 mt-2">Net of VAT</div>
        </div>
        <div className="rounded-lg bg-surface border border-rule p-6 flex flex-col justify-between">
          <div className="text-[10px] uppercase tracking-wider text-ink-3 font-semibold">
            Total invoiced
          </div>
          <Money
            value={data.data?.totals.total ?? '0'}
            className="kc-serif text-3xl text-ink mt-2 block"
          />
          <div className="text-xs text-ink-3 mt-2">Incl. VAT</div>
        </div>
      </div>

      <DataTableCard>
        {data.isLoading || !data.data ? (
          <div className="text-center text-ink-3 px-6 py-12">Loading…</div>
        ) : data.data.lines.length === 0 ? (
          <EmptyState title="No taxable invoices in this period" />
        ) : (
          <table className="w-full text-sm">
            <thead className={STICKY_HEAD}>
              <tr className="text-left text-xs uppercase tracking-wide text-ink-3 border-b border-rule">
                <th className="px-6 py-2 font-medium">Invoice</th>
                <th className="px-6 py-2 font-medium">Date</th>
                <th className="px-6 py-2 font-medium">Shop</th>
                <th className="px-6 py-2 font-medium">Customer</th>
                <th className="px-6 py-2 font-medium text-right">Taxable</th>
                <th className="px-6 py-2 font-medium text-right">VAT</th>
                <th className="px-6 py-2 font-medium text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {data.data.lines.map((l) => (
                <tr key={l.id} className="border-b border-rule-2 hover:bg-paper-2">
                  <td className="px-6 py-3 kc-mono">
                    {l.orderId ? (
                      <Link to={`/orders/${l.orderId}`} className="text-ink hover:text-accent">
                        {l.number}
                      </Link>
                    ) : (
                      <span className="text-ink">{l.number}</span>
                    )}
                    {l.kind === 'credit_note' && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-negative">
                        credit note
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-3">{l.date}</td>
                  <td className="px-6 py-3">
                    <span className="text-xs uppercase tracking-wider text-ink-3">{l.shop}</span>
                  </td>
                  <td className="px-6 py-3">{l.customer}</td>
                  <td className="px-6 py-3 text-right">
                    <Money value={l.subtotal} />
                  </td>
                  <td className="px-6 py-3 text-right">
                    <Money value={l.vat} />
                  </td>
                  <td className="px-6 py-3 text-right">
                    <Money value={l.total} />
                  </td>
                </tr>
              ))}
              <tr className="bg-paper-2">
                <td className="px-6 py-3 font-medium text-ink" colSpan={4}>
                  Total
                </td>
                <td className="px-6 py-3 text-right">
                  <Money value={data.data.totals.subtotal} className="font-medium" />
                </td>
                <td className="px-6 py-3 text-right">
                  <Money value={data.data.totals.vat} className="font-medium text-accent" />
                </td>
                <td className="px-6 py-3 text-right">
                  <Money value={data.data.totals.total} className="font-medium" />
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </DataTableCard>
    </PageScaffold>
  );
}
